import type { HealthCategory } from '@/types/category';

/**
 * Maps blog category slugs to nutrition profiles, objectives, and UI.
 * Used by CategoryLandingPage and Onboarding to pre-fill from blog context.
 */
export const HEALTH_CATEGORIES: Record<string, HealthCategory> = {
  diabetes: {
    slug: 'diabetes',
    profiles: ['type2DiabetesManagement'],
    objective: 'maintain',
    heroTitle: 'Plan nutricional personalizado para diabetes',
    heroSubtitle: 'Controla tu glucemia con un plan diseñado por IA',
    color: 'blue',
    icon: 'activity',
  },
  'higado-graso': {
    slug: 'higado-graso',
    profiles: ['fattyLiverManagement'],
    objective: 'lose',
    heroTitle: 'Cuida tu hígado con alimentación inteligente',
    heroSubtitle: 'Plan adaptado para hígado graso, hecho para ti',
    color: 'green',
    icon: 'heart',
  },
  celiaquia: {
    slug: 'celiaquia',
    profiles: ['glutenFreeDiet'],
    objective: 'maintain',
    heroTitle: 'Come sin gluten, sin complicaciones',
    heroSubtitle: 'Plan 100% libre de gluten, con recetas deliciosas',
    color: 'amber',
    icon: 'wheat-off',
  },
  embarazo: {
    slug: 'embarazo',
    profiles: ['maternalNutritionPregnancy'],
    objective: 'gain',
    heroTitle: 'Nutrición para tu embarazo',
    heroSubtitle: 'Plan personalizado para cada trimestre',
    color: 'pink',
    icon: 'baby',
  },
  lactancia: {
    slug: 'lactancia',
    profiles: ['maternalNutritionLactation'],
    objective: 'maintain',
    heroTitle: 'Nutrición para tu lactancia',
    heroSubtitle: 'Energía y nutrientes para ti y tu bebé',
    color: 'pink',
    icon: 'baby',
  },
  'vida-fitness': {
    slug: 'vida-fitness',
    profiles: ['activeLifestyle'],
    objective: 'maintain',
    heroTitle: 'Alimenta tu rendimiento',
    heroSubtitle: 'Plan nutricional para deportistas',
    color: 'orange',
    icon: 'dumbbell',
  },
  'veganismo-vegetarianismo': {
    slug: 'veganismo-vegetarianismo',
    profiles: ['veganDiet'],
    objective: 'maintain',
    heroTitle: 'Plan vegano completo y equilibrado',
    heroSubtitle: 'Sin deficiencias nutricionales, 100% plant-based',
    color: 'emerald',
    icon: 'leaf',
  },
  'hipertension-arterial': {
    slug: 'hipertension-arterial',
    profiles: ['hypertensionManagement'],
    objective: 'maintain',
    heroTitle: 'Controla tu presión con lo que comes',
    heroSubtitle: 'Plan bajo en sodio, alto en nutrientes',
    color: 'red',
    icon: 'heart-pulse',
  },
  'aumento-masa-muscular': {
    slug: 'aumento-masa-muscular',
    profiles: ['activeLifestyle'],
    objective: 'gain',
    heroTitle: 'Gana masa muscular con la alimentación correcta',
    heroSubtitle: 'Plan hipercalórico personalizado',
    color: 'purple',
    icon: 'trending-up',
  },
  'alimentacion-saludable': {
    slug: 'alimentacion-saludable',
    profiles: ['generalHealthAndWellness'],
    objective: 'maintain',
    heroTitle: 'Mejora tu alimentación de forma sostenible',
    heroSubtitle: 'Plan nutricional balanceado, adaptado a ti',
    color: 'teal',
    icon: 'salad',
  },
  'alergias-alimentarias': {
    slug: 'alergias-alimentarias',
    profiles: ['generalHealthAndWellness'],
    objective: 'maintain',
    heroTitle: 'Come seguro, sin preocuparte por alergias',
    heroSubtitle: 'Plan 100% adaptado excluyendo tus alérgenos',
    color: 'sky',
    icon: 'shield',
  },
  'dieta-mediterranea': {
    slug: 'dieta-mediterranea',
    profiles: ['mediterraneanDiet'],
    objective: 'maintain',
    heroTitle: 'Plan mediterráneo personalizado',
    heroSubtitle: 'La dieta más saludable del mundo, adaptada a ti',
    color: 'cyan',
    icon: 'sun',
  },
  'enfermedad-renal-cronica': {
    slug: 'enfermedad-renal-cronica',
    profiles: ['renalDiseaseManagement'],
    objective: 'maintain',
    heroTitle: 'Cuida tus riñones con alimentación',
    heroSubtitle: 'Plan controlado en sodio, potasio y proteínas',
    color: 'slate',
    icon: 'shield-check',
  },
  'intolerancia-lactosa': {
    slug: 'intolerancia-lactosa',
    profiles: ['lactoseFreeDiet'],
    objective: 'maintain',
    heroTitle: 'Come sin lactosa, sin molestias',
    heroSubtitle: 'Plan 100% libre de lactosa',
    color: 'lime',
    icon: 'milk-off',
  },
  'sindrome-intestino-irritable': {
    slug: 'sindrome-intestino-irritable',
    profiles: ['ibsManagement'],
    objective: 'maintain',
    heroTitle: 'Controla tu intestino con alimentación',
    heroSubtitle: 'Plan bajo FODMAP, menos molestias',
    color: 'violet',
    icon: 'stethoscope',
  },
  ninos: {
    slug: 'ninos',
    profiles: ['pediatricNutrition'],
    objective: 'maintain',
    heroTitle: 'Nutrición para el crecimiento de tus hijos',
    heroSubtitle: 'Menús adaptados para niños, saludables y ricos',
    color: 'yellow',
    icon: 'smile',
  },
  'colesterol-alto': {
    slug: 'colesterol-alto',
    profiles: ['cholesterolManagement'],
    objective: 'maintain',
    heroTitle: 'Baja tu colesterol con alimentación',
    heroSubtitle: 'Plan cardioprotector con omega-3 y fibra soluble',
    color: 'orange',
    icon: 'heart',
  },
  anemia: {
    slug: 'anemia',
    profiles: ['anemiaManagement'],
    objective: 'maintain',
    heroTitle: 'Recupera tu hierro con alimentación',
    heroSubtitle: 'Plan rico en hierro y vitamina C para combatir la anemia',
    color: 'red',
    icon: 'activity',
  },
  menopausia: {
    slug: 'menopausia',
    profiles: ['menopauseNutrition'],
    objective: 'maintain',
    heroTitle: 'Nutrición para la menopausia',
    heroSubtitle: 'Fitoestrógenos, calcio y bienestar hormonal en un plan',
    color: 'rose',
    icon: 'sparkles',
  },
  tiroides: {
    slug: 'tiroides',
    profiles: ['thyroidManagement'],
    objective: 'maintain',
    heroTitle: 'Cuida tu tiroides con lo que comes',
    heroSubtitle: 'Plan con yodo, selenio y alimentos tiroideos seguros',
    color: 'cyan',
    icon: 'shield-check',
  },
  'acido-urico': {
    slug: 'acido-urico',
    profiles: ['goutManagement'],
    objective: 'maintain',
    heroTitle: 'Controla el ácido úrico desde tu plato',
    heroSubtitle: 'Plan bajo en purinas para reducir la gota y las crisis',
    color: 'amber',
    icon: 'shield',
  },
  estrenimiento: {
    slug: 'estrenimiento',
    profiles: ['digestiveHealth'],
    objective: 'maintain',
    heroTitle: 'Mejora tu tránsito intestinal',
    heroSubtitle: 'Plan con fibra, probióticos e hidratación estratégica',
    color: 'lime',
    icon: 'stethoscope',
  },
  // ── Perfiles nuevos del relanzamiento (ids canónicos de EnumProfile) ──
  prediabetes: {
    slug: 'prediabetes',
    profiles: ['prediabetes'],
    objective: 'lose',
    heroTitle: 'Revertí la prediabetes con alimentación',
    heroSubtitle: 'Plan de baja carga glucémica para estabilizar tu glucosa',
    color: 'blue',
    icon: 'activity',
  },
  'sindrome-metabolico': {
    slug: 'sindrome-metabolico',
    profiles: ['metabolicSyndrome'],
    objective: 'lose',
    heroTitle: 'Plan para el síndrome metabólico',
    heroSubtitle: 'Mediterráneo, menos azúcar añadida y menos grasa visceral',
    color: 'orange',
    icon: 'heart-pulse',
  },
  glp1: {
    slug: 'glp1',
    profiles: ['glp1Companion'],
    objective: 'lose',
    heroTitle: 'Nutrición durante tu tratamiento GLP-1',
    heroSubtitle: 'Proteína y fuerza para preservar tu masa muscular',
    color: 'teal',
    icon: 'shield-check',
  },
  sop: {
    slug: 'sop',
    profiles: ['pcos'],
    objective: 'lose',
    heroTitle: 'Plan para el SOP',
    heroSubtitle: 'Baja carga glucémica y antiinflamatorio para tu ciclo',
    color: 'rose',
    icon: 'sparkles',
  },
  endometriosis: {
    slug: 'endometriosis',
    profiles: ['endometriosis'],
    objective: 'maintain',
    heroTitle: 'Alimentación antiinflamatoria para la endometriosis',
    heroSubtitle: 'Omega-3 y menos grasas trans para acompañar el dolor',
    color: 'pink',
    icon: 'heart',
  },
  sibo: {
    slug: 'sibo',
    profiles: ['sibo'],
    objective: 'maintain',
    heroTitle: 'Plan FODMAP para el SIBO',
    heroSubtitle: 'Por fases, para calmar la distensión sin perder nutrientes',
    color: 'violet',
    icon: 'stethoscope',
  },
  reflujo: {
    slug: 'reflujo',
    profiles: ['gerd'],
    objective: 'maintain',
    heroTitle: 'Plan para el reflujo (ERGE)',
    heroSubtitle: 'Menos disparadores, mejor horario, menos ardor',
    color: 'amber',
    icon: 'stethoscope',
  },
  eii: {
    slug: 'eii',
    profiles: ['ibd'],
    objective: 'maintain',
    heroTitle: 'Plan para la EII (Crohn/colitis)',
    heroSubtitle: 'Adaptado a brote y remisión, con supervisión clínica',
    color: 'slate',
    icon: 'shield',
  },
  'envejecimiento-saludable': {
    slug: 'envejecimiento-saludable',
    profiles: ['healthyAgingSarcopenia'],
    objective: 'maintain',
    heroTitle: 'Envejecé con fuerza',
    heroSubtitle: 'Proteína y leucina para prevenir la sarcopenia',
    color: 'emerald',
    icon: 'trending-up',
  },
  default: {
    slug: 'default',
    profiles: ['generalHealthAndWellness'],
    objective: 'maintain',
    heroTitle: 'Tu plan nutricional personalizado',
    heroSubtitle: 'Creado por IA, adaptado a tu cuerpo y objetivos',
    color: 'brand',
    icon: 'sparkles',
  },
} as const;

export function getCategoryBySlug(slug: string): HealthCategory {
  return HEALTH_CATEGORIES[slug] ?? HEALTH_CATEGORIES.default;
}

/**
 * Vida category slug → stable condition tag id (mirrors the Engine condition-taxonomy).
 * Used to build feed boost tags from a secondary profile so its articles float up.
 * Only conditions whose articles carry a condition tag are listed.
 */
export const SLUG_TO_CONDITION_TAG: Record<string, string> = {
  'colesterol-alto': 'dyslipidemia',
  prediabetes: 'prediabetes',
  'sindrome-metabolico': 'metabolicSyndrome',
  glp1: 'glp1Companion',
  sop: 'pcos',
  menopausia: 'menopause',
  endometriosis: 'endometriosis',
  sibo: 'sibo',
  reflujo: 'gerd',
  eii: 'ibd',
  tiroides: 'hypothyroidism',
  'acido-urico': 'gout',
  'envejecimiento-saludable': 'healthyAging',
  lactancia: 'lactation',
  'sindrome-intestino-irritable': 'irritableBowelSyndromeManagement',
};

export function getConditionTag(slug: string): string | undefined {
  return SLUG_TO_CONDITION_TAG[slug];
}

/**
 * Dietary-evidence strength per condition (mirrors the Engine condition-taxonomy / clinical spec §6).
 * 'limited' conditions MUST show an explicit "this is information, not a treatment" disclaimer in the
 * education surface (endometriosis/SOP/SIBO/GLP-1 — expert opinion, no superior diet, no RCT).
 */
export type EvidenceStrength = 'strong' | 'moderate' | 'limited';

export const SLUG_EVIDENCE: Record<string, EvidenceStrength> = {
  'colesterol-alto': 'strong',
  prediabetes: 'strong',
  'sindrome-metabolico': 'moderate',
  glp1: 'limited',
  sop: 'limited',
  menopausia: 'moderate',
  endometriosis: 'limited',
  sibo: 'limited',
  reflujo: 'moderate',
  eii: 'strong',
  tiroides: 'strong',
  'acido-urico': 'strong',
  'envejecimiento-saludable': 'strong',
  lactancia: 'moderate',
};

export function getEvidence(slug: string): EvidenceStrength | undefined {
  return SLUG_EVIDENCE[slug];
}

/**
 * Maps a granular conditionDetails value (from onboarding) to the article sub-type/phase tag, so the
 * feed boosts phase-specific content (IBD flare↔remission, GLP-1 titration↔maintenance, dyslipidemia
 * LDL↔TG). Tolerant of value-naming variants. Mirrors Engine CONDITION_SUBTYPES tags.
 */
const DETAIL_VALUE_TO_TAG: Record<string, string> = {
  // IBD phase
  flare: 'ibd-flare',
  active: 'ibd-flare',
  brote: 'ibd-flare',
  remission: 'ibd-remission',
  remision: 'ibd-remission',
  // GLP-1 phase
  titration: 'glp1-titration',
  titulacion: 'glp1-titration',
  maintenance: 'glp1-maintenance',
  mantenimiento: 'glp1-maintenance',
  // Dyslipidemia pattern
  ldl: 'dyslipidemia-ldl',
  ldlHigh: 'dyslipidemia-ldl',
  tgHigh: 'dyslipidemia-tg',
  veryHighTG: 'dyslipidemia-tg',
  mixed: 'dyslipidemia-ldl',
};

export function detailValueToTag(value: string): string | undefined {
  return DETAIL_VALUE_TO_TAG[value];
}
